import {
  buildIntensity,
  buildWorkForCell,
  parseRepsToken,
  amrapOf,
  isLoadBearingKind,
  type SetIntensity,
  type MovementPattern,
  type SetRowDraft,
  type SlotDraft,
  type UnitsPreference,
} from '@jackedlogs/shared';

import { catalogLoadedSides, deriveEntryKind } from './deriveEntryKind';
import { canonicalRirFromNumbers } from './state/canonicalRir';
import { emptySet } from './state/loggerDraftHelpers';
import type { DraftEntry, DraftSet, WeightUnit } from './state/loggerDraftTypes';

/** PE-22 K1 — the adapter that carries the load AXIS from the program
 *  editor's live draft into the Logger card, so the same card can render a
 *  prescription instead of a measurement.
 *
 *  ⛔ WHY THIS IS NOT `planStarterHydration`. That path is the other
 *  producer of a prescribed DraftEntry, and it is LOSSY BY CONSTRUCTION:
 *  `PlanStarter.entries[].sets[]` carries a `weightKg` that has ALREADY been
 *  resolved and no `intensityKind` at all, so from "100 kg" nothing
 *  downstream can tell whether the coach wrote 100, or 80% of a max, or
 *  "whatever you lifted last time". Recycling it would have shipped a card
 *  that cannot show the axis it is supposed to let you edit.
 *
 *  ⛔ AND WHY IT READS `SlotDraft`, NOT the stored `Slot`. The composer's
 *  live editing model already holds the unresolved axis per week —
 *  `SetRowDraft.intensityKind` on the row, `CellDraft.intensityValue` on the
 *  cell. Building against the raw stored `Slot` instead would give the card
 *  a SECOND program save-path running beside `programEditActions`, two
 *  copies free to drift.
 *
 *  ⭐ THE WRITE DIRECTION DELIBERATELY DOES NOT LIVE HERE. A card edit goes
 *  back through the composer's `updateCell` — the axis returns to
 *  `{kind, intensityValue}` via `intensityToCell`, the exact inverse of the
 *  `buildIntensity` used below. This module is the READ half only, and that
 *  asymmetry is the point: one save-path, not two.
 */

/** One row of the slot at one week, as the card's prescribed set.
 *
 *  Returns the set plus the intensity so the caller can gate on the axis
 *  without re-deriving it. A cell mid-edit ("8." typed but not finished)
 *  makes `buildIntensity` return an error shape; the axis is then simply
 *  ABSENT rather than wrong — a card that blanks a field is recoverable, a
 *  card that shows a number the coach never wrote is not.
 */
const setFromRow = (
  row: SetRowDraft,
  weekIndex: number,
  cellUnit: UnitsPreference,
  unit: WeightUnit,
): DraftSet | null => {
  const cell = row.cells[weekIndex];
  if (!cell) return null;

  const repsToken = parseRepsToken(cell.reps);
  const amrap = amrapOf(cell);
  const built = buildIntensity(row.intensityKind, cell.intensityValue, cellUnit);
  const intensity: SetIntensity | undefined =
    'kind' in built ? built : undefined;

  // The time axis and the rest come from the shared cell builder, the same
  // one buildPayload uses, so a countdown bout means here exactly what it
  // will mean when the program is saved.
  const { work, restSec } = buildWorkForCell(
    row.workKind,
    cell.workDurationSec,
    cell.restSec,
  );

  // ⛔ The MEASUREMENT half stays empty. Nothing on a prescribed set has
  // been performed, and seeding these is how a prescription became a
  // measurement everywhere else in this codebase.
  const base = emptySet(unit);

  // ⭐ PE-34 — the ONE exception, and the reason the load was un-writable for
  // the whole life of this editor: the weight FIELD is where the coach types
  // the prescribed load. It was bound to `weight`, which is the measurement
  // half and is empty here by the rule above — so the number went in and came
  // back blank at the next render. Binding the field to the prescribed value
  // makes it an authoring field in prescribe, which is what this card IS.
  // ⛔ Deliberately NOT converted: `intensityValue` is expressed in cellUnit
  // (the unit the draft was seeded in) and the field is labelled with that
  // same unit, so there is no conversion to round-trip on every keystroke —
  // the classic composer edits the same value the same way.
  // Only the two axes that carry a number: on history/sforzo/none there is
  // nothing to type, and a live field there would swallow again.
  const prescribedLoad = isLoadBearingKind(row.intensityKind)
    ? cell.intensityValue
    : '';

  // The effort MIRROR, not a second source of truth: the band reads
  // `rirPrescribed` to draw its dashed target, and it is derived from the
  // axis rather than stored beside it, so the two can never disagree.
  // ⛔ Only when effort IS the axis — a set prescribed at 80% carries no
  // effort target, and drawing one there would invent a second prescription
  // the coach did not write.
  const rirPrescribed =
    intensity?.kind === 'rir'
      ? canonicalRirFromNumbers(intensity.target, undefined)
      : intensity?.kind === 'rpe'
        ? canonicalRirFromNumbers(undefined, intensity.target)
        : null;

  return {
    ...base,
    ...(prescribedLoad !== '' ? { weight: prescribedLoad } : {}),
    // ⭐ PE-42 — this hydration serves the PRESCRIBE editor only (the K1
    // adapter and the roster; the logger has its own paths). So the reps cell
    // keeps the coach's number even on an AMRAP: here the field is where the
    // prescription is WRITTEN, not where an outcome is awaited.
    // ⛔ That is why `amrapFloor` is NOT set — it drives the "at least N"
    // PLACEHOLDER of an empty reps field (FocusPanelInputs.tsx:214), which is
    // the logger's question to the athlete. Setting it here would blank the
    // coach's 8 the moment the AMRAP chip was tapped: the swallowing field
    // this whole slice exists to remove.
    reps: repsToken ? String(repsToken.reps) : '',
    amrapPrescribed: amrap,
    ...(intensity ? { intensityPrescribed: intensity } : {}),
    ...(rirPrescribed !== null ? { rirPrescribed } : {}),
    ...(work !== undefined ? { workPrescribed: work } : {}),
    ...(restSec !== undefined ? { restPrescribedSec: restSec } : {}),
    // K3.4 — the "Nota" chip's read half. Empty string reads as "nothing
    // written" (VALUELESS, same convention as an unset field elsewhere in
    // this file), so an empty note never renders the chip selected.
    ...(row.notesPerCell[weekIndex]
      ? { notePrescribed: row.notesPerCell[weekIndex] }
      : {}),
  };
};

/** A slot of the composer draft, at one week, as a Logger entry.
 *
 *  Returns `null` when the slot does not run that week (`inactiveWeeks`),
 *  which is a real state of the matrix and not an error: the card shows
 *  nothing because the coach turned that day off.
 */
export const slotDraftToEntry = (
  slot: SlotDraft,
  weekIndex: number,
  opts: {
    /** The catalog row for `slot.exerciseId`. Absent is a REAL case, not a
     *  bug: the composer can hold a slug the catalog hasn't loaded (or no
     *  longer carries), and `deriveEntryKind` already answers for that with
     *  the same fallbacks add + swap use. */
    exercise?: {
      name?: string;
      isBodyweight?: boolean | null;
      movementPattern?: MovementPattern | null;
      isTimeBased?: boolean | null;
      unilateral?: boolean | null;
    };
    /** The unit the DRAFT was seeded in — see buildIntensity. Never the
     *  profile's current preference, or the two directions disagree. */
    cellUnit?: UnitsPreference;
    /** The unit the CARD renders in. Independent of `cellUnit`: the draft
     *  can be stored in kg and shown in lb. */
    displayUnit?: WeightUnit;
  } = {},
): DraftEntry | null => {
  if (slot.inactiveWeeks[weekIndex]) return null;
  const cellUnit = opts.cellUnit ?? 'kg';
  const unit = opts.displayUnit ?? 'kg';
  const ex = opts.exercise;

  // Same catalog→kind rule as add + swap, through the one shared seam, so a
  // prescribed row and a logged row of the same exercise can never disagree
  // about what kind of exercise it is.
  const { isBodyweight, isCardio, isTimeBased, movementPattern } =
    deriveEntryKind({
      isBodyweight: ex?.isBodyweight,
      movementPattern: ex?.movementPattern ?? null,
      isTimeBased: ex?.isTimeBased,
    });

  const sets = slot.setRows
    .map((row) => setFromRow(row, weekIndex, cellUnit, unit))
    .filter((s): s is DraftSet => s !== null);

  return {
    entryId: slot.id,
    exerciseId: slot.exerciseId,
    // The draft's own name wins over the catalog's: the composer already
    // resolved it once, and a slot renamed in the editor should read the
    // way the editor shows it.
    exerciseName: slot.exerciseName ?? ex?.name ?? slot.exerciseId,
    isBodyweight,
    isCardio,
    isTimeBased,
    movementPattern,
    loadedSides: catalogLoadedSides({
      movementPattern,
      unilateral: ex?.unilateral,
    }),
    // The composer's grouping carries over verbatim except `cadence`, which
    // is authoring state the Logger card has no vocabulary for; the slot
    // keeps it and the save path re-emits it untouched. The NAME does come
    // across (PE-30): both surfaces label a group through `groupLabelFor`,
    // which falls back to the numbered default only when there is no name —
    // so dropping it here would show "Superset 1" over a group the coach had
    // just named, in the very editor where they named it.
    group: slot.group
      ? {
          id: slot.group.id,
          kind: slot.group.kind,
          ...(slot.group.name !== undefined ? { name: slot.group.name } : {}),
        }
      : null,
    // The slot's own flags, carried so the card opens with the column the
    // coach chose rather than re-deriving it from the catalog.
    effortColumn: slot.rpeEnabled || slot.rirEnabled,
    historyOpen: false,
    // EQ-1 — a prescription names no machine and no place: those are facts
    // about where a set was PERFORMED, and nothing has been performed.
    equipmentInstance: null,
    location: null,
    // PE-37 — the per-EXERCISE note travels into the card. ⛔ The READ half is
    // not optional: the write alone would let the coach type a note into a
    // field that shows nothing back, which is the shape PE-34 just cost a
    // slice to learn [[feedback_an_input_is_not_wired_until_it_shows_what_it_wrote]].
    // Omitted when empty, so an entry with no note carries no key at all —
    // the same VALUELESS convention the rest of this file uses.
    ...(slot.instructions.trim() ? { notePrescribed: slot.instructions } : {}),
    sets,
  };
};
